import { useTranslation } from '@eti/providers';
import { price as priceUtils } from '@eti/utils';
import { css, mediaQueries } from '@eti/styles';
import { useRef } from 'react';
import { useLinksAccessibility } from '../../custom-hooks/useLinkAccessibility';
import { isNoOption } from '../utils/units';
import TravelerDescription from '../../common/components/TravelerDescription';
import ProductLayout from '../../common/main-layout/ProductLayout';
import { MEAL } from '../../products';
import { getMinMealPrice, getTotalPrice, getTravelerMealPreferenceList, sortTravelers } from '../utils/mealUtil';
import { getReadMoreLabel, getUpsellProps } from '../../common/utils/productCardProps';
import { useIllustration } from '../../common/utils/useIllustration';
import getTranslatedTexts from '../../common/text-resolvers/utils/getTranslatedTexts';
import { useProductSnowplowTracking } from '../../custom-hooks/useProductSnowplowTracking';
import MealDropdown from './MealDropdown';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const {
  name: PRODUCT_NAME,
  id: PRODUCT_ID
} = MEAL;
const travelerDescriptionWrapperStyles = css`
  margin: 14px 0 10px;
`;
const mealCtaGridLayoutStyle = css`
  column-gap: 8px;
  display: grid;
  grid-template-columns: 2fr auto;
  grid-template-rows: repeat(3, auto);

  .priceLabel {
    grid-area: 1 / 1 / 1 / 3;

    @media ${mediaQueries.small.only} {
      grid-area: 1 / 1 / 1 / 3;
    }
  }

  .price {
    grid-area: 2 / 1 / 3 / 3;
    max-height: 18px;

    @media ${mediaQueries.small.only} {
      grid-area: 2 / 1 / 3 / 3;
    }
  }
`;
const texts = {
  description: 'mealAbstractText',
  fromPriceLabel: 'Products.Price.Label.From',
  noOptionLabel: 'Product.Meal.Dropdown.NoOption',
  readMoreContent: 'mealReadMoreModalContent',
  title: 'mealTitle',
  totalPriceLabel: 'Products.Price.Label.Total',
  travelerDescription: 'Postbooking.Traveler.Description',
  upSell: 'mealUpSellText'
};
const Meal = _ref => {
  let {
    form,
    mealState,
    order,
    productPosition,
    travelerNames
  } = _ref;
  const {
    t
  } = useTranslation();
  const rootElement = useRef(null);
  const {
    description,
    fromPriceLabel,
    noOptionLabel,
    readMoreContent,
    title,
    totalPriceLabel,
    travelerDescription,
    upSell
  } = getTranslatedTexts(texts, t);
  useProductSnowplowTracking(PRODUCT_NAME, {
    productPosition,
    rootElement
  });
  useLinksAccessibility({
    targetElement: rootElement,
    productTitle: title
  });
  const travelers = getTravelerMealPreferenceList(order, travelerNames, travelerDescription, noOptionLabel);
  const isMealSelected = mealState.filter(meal => !isNoOption(meal.value)).length > 0;
  const mealPriceRaw = isMealSelected ? getTotalPrice(mealState) : getMinMealPrice(travelers.flatMap(traveler => traveler.mealPreferences));
  const isMultipleTravelers = travelers.length > 1;
  const sortedTravelers = isMultipleTravelers ? sortTravelers(travelers) : travelers;
  const sortedTravelersWithMeals = sortedTravelers.filter(traveler => traveler.mealPreferences.length > 0);
  const {
    upSellObject,
    isUpsellHighlight = false,
    shouldDisplayUpsell = false
  } = getUpsellProps(upSell);
  const readMoreButtonLabel = getReadMoreLabel(t);
  const illustrationUrl = useIllustration(PRODUCT_NAME);
  const productData = {
    description,
    form,
    illustrationUrl,
    isHighlighted: isUpsellHighlight,
    isSelected: isMealSelected,
    price: priceUtils.formatPrice(mealPriceRaw),
    priceLabel: isMealSelected ? totalPriceLabel : fromPriceLabel,
    priceRaw: mealPriceRaw !== null && mealPriceRaw !== void 0 ? mealPriceRaw : 0,
    productId: PRODUCT_ID,
    productName: PRODUCT_NAME,
    readMoreButtonLabel,
    readMoreContent,
    shouldDisplayUpsell,
    title,
    travelers: order.travelers,
    upSell: upSellObject
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: (sortedTravelersWithMeals === null || sortedTravelersWithMeals === void 0 ? void 0 : sortedTravelersWithMeals.length) > 0 && /*#__PURE__*/_jsx(ProductLayout, {
      productData: productData,
      ref: rootElement,
      children: /*#__PURE__*/_jsx(ProductLayout.Cta, {
        customGrid: mealCtaGridLayoutStyle,
        children: /*#__PURE__*/_jsx("ul", {
          children: sortedTravelersWithMeals.map(traveler => /*#__PURE__*/_jsxs("li", {
            "data-testid": `${MEAL.name}-traveler-${traveler.travelerId}`,
            children: [isMultipleTravelers && /*#__PURE__*/_jsx("div", {
              className: travelerDescriptionWrapperStyles,
              children: /*#__PURE__*/_jsx(TravelerDescription, {
                name: (traveler === null || traveler === void 0 ? void 0 : traveler.name) || '',
                type: traveler === null || traveler === void 0 ? void 0 : traveler.travelerType
              })
            }), /*#__PURE__*/_jsx(MealDropdown, {
              currentTravelerId: traveler === null || traveler === void 0 ? void 0 : traveler.travelerId,
              mealPreferences: (traveler === null || traveler === void 0 ? void 0 : traveler.mealPreferences) || [],
              mealState: mealState,
              productName: PRODUCT_NAME,
              productPosition: productPosition,
              rootElement: rootElement
            })]
          }, traveler.travelerId))
        })
      })
    })
  });
};
export default Meal;
//# sourceMappingURL=Meal.js.map